import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useSuspenseQuery} from '@github-ui/react-query'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {
  type CertificateAttributes,
  type CommitSignatureType,
  type SignatureResult,
  SignedCommitBadge,
  type VerificationStatus,
} from '@github-ui/signed-commit-badge'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {Suspense} from 'react'
import {usePreloadedQuery} from 'react-relay'

import {VALUES} from '../constants/values'
import type {secondaryTimelineQuery} from './__generated__/secondaryTimelineQuery.graphql'
import {ReferencedEventVerificationStatusFailure} from './ReferencedEventVerificationStatusFailure'
import {ReferencedEventVerificationStatusSkeleton} from './ReferencedEventVerificationStatusSkeleton'
import {type PreloadedSecondaryQueryType, SecondaryTimelineGraphqlQuery} from './secondary-timeline-query'
import {
  type CertificateAttributesFromFragment,
  fetchReferencedEventCommits,
  type ReferencedEventCommitNode,
  referencedEventCommitsQueryKey,
  type SecondaryTimelineEndpointArgs,
  type SecondaryTimelineSource,
  type SignatureResultFromFragment,
} from './secondary-timeline-source'

export type ReferencedEventVerificationStatusInnerFragment = RelayCompat<
  Pick<ReferencedEventCommitNode, 'abbreviatedOid' | 'hasSignature' | 'verificationStatus' | 'signature'>
>

type ReferencedEventVerificationStatusProps = {
  secondaryTimelineSource?: SecondaryTimelineSource
  eventId: string
  viewerLogin: string | null
}

export function ReferencedEventVerificationStatus({
  secondaryTimelineSource,
  eventId,
  viewerLogin,
}: ReferencedEventVerificationStatusProps): React.ReactElement | null {
  if (!secondaryTimelineSource) return null

  return (
    <ErrorBoundary boundaryName="referenced-event-verification" fallback={<ReferencedEventVerificationStatusFailure />}>
      <Suspense fallback={<ReferencedEventVerificationStatusSkeleton />}>
        {secondaryTimelineSource.kind === 'relay' ? (
          <RelayCommitSignature
            queryRef={secondaryTimelineSource.queryRef}
            eventId={eventId}
            viewerLogin={viewerLogin}
          />
        ) : (
          <EndpointCommitSignature source={secondaryTimelineSource} eventId={eventId} viewerLogin={viewerLogin} />
        )}
      </Suspense>
    </ErrorBoundary>
  )
}

function RelayCommitSignature({
  queryRef,
  eventId,
  viewerLogin,
}: {
  queryRef: PreloadedSecondaryQueryType
  eventId: string
  viewerLogin: string | null
}): React.ReactElement | null {
  const {nodes} = usePreloadedQuery<secondaryTimelineQuery>(SecondaryTimelineGraphqlQuery, queryRef)
  const commitNode = nodes?.find(node => node?.__id === eventId)

  if (!commitNode?.commit) return null

  // RELAY-CLEANUP(ReferencedEventVerificationStatus): remove cast when the Relay source is dropped
  return (
    <ReferencedEventVerificationStatusInner
      commit={commitNode.commit as ReferencedEventVerificationStatusInnerFragment}
      viewerLogin={viewerLogin}
    />
  )
}

function EndpointCommitSignature({
  source,
  eventId,
  viewerLogin,
}: {
  source: SecondaryTimelineEndpointArgs
  eventId: string
  viewerLogin: string | null
}): React.ReactElement | null {
  // The queryFn runs during render and needs `window`. The server renders the same skeleton the
  // Suspense fallback shows, so hydration matches.
  const {isBrowser} = useRuntimeEnvironment()
  if (!isBrowser) return <ReferencedEventVerificationStatusSkeleton />

  return <EndpointCommitSignatureFetched source={source} eventId={eventId} viewerLogin={viewerLogin} />
}

function EndpointCommitSignatureFetched({
  source,
  eventId,
  viewerLogin,
}: {
  source: SecondaryTimelineEndpointArgs
  eventId: string
  viewerLogin: string | null
}): React.ReactElement | null {
  // eslint-disable-next-line @github-ui/github-monorepo/require-query-persister -- signature verification is re-read per visit; nothing here is worth persisting across sessions
  const {data: nodes} = useSuspenseQuery({
    queryKey: referencedEventCommitsQueryKey(source),
    queryFn: ({signal}) => fetchReferencedEventCommits(source, signal),
  })

  const commit = nodes.find(node => node.id === eventId)?.commit
  if (!commit) return null

  return <ReferencedEventVerificationStatusInner commit={commit} viewerLogin={viewerLogin} />
}

function ReferencedEventVerificationStatusInner({
  commit,
  viewerLogin,
}: {
  commit: ReferencedEventVerificationStatusInnerFragment
  viewerLogin: string | null
}) {
  if (
    !commit ||
    !commit.signature ||
    !commit.verificationStatus ||
    commit.verificationStatus === 'UNSIGNED' ||
    !commit.abbreviatedOid ||
    commit.hasSignature === undefined
  ) {
    return null
  }
  return (
    <SignedCommitBadge
      commitOid={commit.abbreviatedOid}
      hasSignature={commit.hasSignature}
      verificationStatus={getVerificationStatus(commit.verificationStatus)}
      // RELAY-CLEANUP(ReferencedEventVerificationStatus): remove cast when the Relay source is dropped
      signature={getSignatureResult(commit.signature as SignatureResultFromFragment, viewerLogin)}
    />
  )
}

function getVerificationStatus(verificationStatus: string): VerificationStatus {
  switch (verificationStatus) {
    case 'VERIFIED':
      return 'verified'
    case 'UNVERIFIED':
      return 'unverified'
    case 'PARTIALLY_VERIFIED':
      return 'partially_verified'
    default:
      return 'unsigned'
  }
}

function getSignatureResult(signature: SignatureResultFromFragment, viewerLogin: string | null): SignatureResult {
  return {
    hasSignature: true,
    helpUrl: VALUES.commitBadgeHelpUrl,
    isViewer: !!signature.signer && viewerLogin === signature.signer.login,
    keyExpired: signature.state === 'EXPIRED_KEY',
    // keyId isn't used for S/MIME signatures
    keyId:
      (signature.__typename === 'GpgSignature'
        ? signature.keyId
        : signature.__typename === 'SshSignature'
          ? signature.keyFingerprint
          : null) ?? '',
    keyRevoked: signature.state === 'OCSP_REVOKED',
    signedByGitHub: signature.wasSignedByGitHub,
    signerLogin: signature.signer?.login ?? '',
    signerAvatarUrl: signature.signer?.avatarUrl ?? '',
    signatureType: signature.__typename as CommitSignatureType,
    signatureCertificateSubject: toCertificateAttributes(
      signature.__typename === 'SmimeSignature' ? signature.subject : null,
    ),
    signatureCertificateIssuer: toCertificateAttributes(
      signature.__typename === 'SmimeSignature' ? signature.issuer : null,
    ),
    signatureVerificationReason: signature.state,
  }
}

function toCertificateAttributes(attributes?: CertificateAttributesFromFragment | null): CertificateAttributes {
  if (!attributes) {
    return {}
  }

  return {
    common_name: attributes.commonName ?? undefined,
    email_address: attributes.emailAddress ?? undefined,
    organization: attributes.organization ?? undefined,
    organization_unit: attributes.organizationUnit ?? undefined,
  }
}
